import React, { useMemo, useState } from 'react'
import { DateTime, Interval } from 'luxon'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import Box from '@material-ui/core/Box'
import Divider from '@material-ui/core/Divider'
import FormControl from '@material-ui/core/FormControl'
import Select from '@material-ui/core/Select'
import MenuItem from '@material-ui/core/MenuItem'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import Dialog from 'components/dialog'
import AccessTimeIcon from 'components/material-symbol/icons/AccessTime'
import LocationOnIcon from 'components/material-symbol/icons/LocationOn'
import VisibilityIcon from 'components/material-symbol/icons/Visibility'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { useStyles as useEditFormStyles } from 'pages/pga-coach/bookings/events/edit/edit-event-form/styles'
import {
  CALLOUT_BACKGROUND,
  CALLOUT_FOREGROUND,
  OUTLINED_INPUT_BORDER,
} from 'lib/form-field-styles'
import { formatIncrementLabel } from 'pages/pga-coach/bookings/booking-types/components/start-time-increment-field'

const AVAILABILITY_START = { hours: 9 }
const AVAILABILITY_END = { hours: 18 }
const AVAILABILITY_LABEL = '9:00 AM – 6:00 PM'

const useStyles = makeStyles(theme => ({
  dialogTitle: {
    paddingRight: theme.spacing(6),
  },
  incrementRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginTop: theme.spacing(1),
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
  },
  incrementChip: {
    borderRadius: 24,
    border: `1px solid ${OUTLINED_INPUT_BORDER}`,
    textTransform: 'none',
    fontWeight: 400,
    fontSize: '0.875rem',
    color: CALLOUT_FOREGROUND,
    padding: theme.spacing(1.5, 3),
    minWidth: 88,
    backgroundColor: '#FFFFFF',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#FFFFFF',
      borderColor: OUTLINED_INPUT_BORDER,
    },
  },
  incrementChipSelected: {
    backgroundColor: CALLOUT_BACKGROUND,
    color: CALLOUT_FOREGROUND,
    borderColor: 'transparent',
    '&:hover': {
      backgroundColor: CALLOUT_BACKGROUND,
      borderColor: 'transparent',
    },
  },
  sectionDivider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  timeSlotRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  timeSlot: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 72,
    padding: theme.spacing(1, 2),
    borderRadius: 24,
    border: `1px solid ${OUTLINED_INPUT_BORDER}`,
    fontSize: '0.875rem',
    color: CALLOUT_FOREGROUND,
    lineHeight: 1.25,
    backgroundColor: '#FFFFFF',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(3),
    marginTop: theme.spacing(1),
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  footerMeta: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    fontSize: '0.8125rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.4,
  },
  footerIcon: {
    fontSize: 16,
    color: theme.palette.text.secondary,
  },
  triggerBtn: {
    fontSize: '15px',
    padding: '8px 11px',
    textTransform: 'none',
  },
  linkTrigger: {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.875rem',
    padding: 0,
    minWidth: 0,
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  availabilityRange: {
    color: CALLOUT_FOREGROUND,
    fontWeight: 600,
  },
}))

const StartTimeIncrementPreviewDialog = ({
  startTimeIncrementOptions,
  linkTrigger = false,
}) => {
  const classes = useStyles()
  const editClasses = useEditFormStyles()
  const defaultIncrement =
    startTimeIncrementOptions.find(option => option.default)?.value ?? 30
  const [selectedIncrementValue, setSelectedIncrementValue] = useState(
    defaultIncrement,
  )
  const isMobile = useMediaQuery(useTheme().breakpoints.down('xs'))

  const amStartTimes = useMemo(
    () =>
      Interval.fromDateTimes(
        DateTime.now()
          .startOf('day')
          .plus(AVAILABILITY_START),
        DateTime.now()
          .startOf('day')
          .plus({ hours: 11, minutes: 59 }),
      )
        .splitBy({ minutes: selectedIncrementValue })
        .map(interval => interval.start),
    [selectedIncrementValue],
  )

  const pmStartTimes = useMemo(
    () =>
      Interval.fromDateTimes(
        DateTime.now()
          .startOf('day')
          .plus({ hours: 12 }),
        DateTime.now()
          .startOf('day')
          .plus(AVAILABILITY_END),
      )
        .splitBy({ minutes: selectedIncrementValue })
        .map(interval => interval.start),
    [selectedIncrementValue],
  )

  const availableStartTimesCount = amStartTimes.length + pmStartTimes.length

  return (
    <Box>
      <Dialog
        showCloseButton
        fullWidth
        trigger={
          linkTrigger ? (
            <Button color="primary" className={classes.linkTrigger}>
              <VisibilityIcon style={{ fontSize: 18, marginRight: 6 }} />
              Preview Sample Calendar
            </Button>
          ) : (
            <Button color="primary" size="small" className={classes.triggerBtn}>
              View Sample Calendar
            </Button>
          )
        }
        maxWidth="sm"
      >
        {() => (
          <>
            <DialogTitle className={classes.dialogTitle}>
              Sample Calendar
            </DialogTitle>
            <DialogContent>
              <Typography className={editClasses.fieldHelper}>
                Choose an increment to preview the start times students will
                see, based on availability of{' '}
                <span className={classes.availabilityRange}>
                  {AVAILABILITY_LABEL}.
                </span>
              </Typography>

              <Box className={editClasses.fieldGroup}>
                <Typography className={editClasses.sectionHeading}>
                  Increment
                </Typography>
                {isMobile ? (
                  <FormControl fullWidth variant="outlined" size="small">
                    <Select
                      onChange={event =>
                        setSelectedIncrementValue(event.target.value)
                      }
                      value={selectedIncrementValue}
                    >
                      {startTimeIncrementOptions.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                          {formatIncrementLabel(option.label)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <Box className={classes.incrementRow}>
                    {startTimeIncrementOptions.map(option => {
                      const isSelected = option.value === selectedIncrementValue

                      return (
                        <Button
                          key={option.value}
                          type="button"
                          className={`${classes.incrementChip} ${
                            isSelected ? classes.incrementChipSelected : ''
                          }`}
                          onClick={() =>
                            setSelectedIncrementValue(option.value)
                          }
                        >
                          {formatIncrementLabel(option.label)}
                        </Button>
                      )
                    })}
                  </Box>
                )}
              </Box>

              <Divider className={classes.sectionDivider} />

              <Box className={editClasses.fieldGroup}>
                <Typography className={editClasses.sectionHeading}>
                  Morning
                </Typography>
                <Box className={classes.timeSlotRow}>
                  {amStartTimes.map(startTime => (
                    <Box
                      key={startTime.toISO()}
                      className={classes.timeSlot}
                      component="span"
                    >
                      {startTime.toFormat('hh:mm')}
                    </Box>
                  ))}
                </Box>
              </Box>

              <Box className={editClasses.fieldGroup}>
                <Typography className={editClasses.sectionHeading}>
                  Afternoon
                </Typography>
                <Box className={classes.timeSlotRow}>
                  {pmStartTimes.map(startTime => (
                    <Box
                      key={startTime.toISO()}
                      className={classes.timeSlot}
                      component="span"
                    >
                      {startTime.toFormat('hh:mm')}
                    </Box>
                  ))}
                </Box>
              </Box>

              <Box className={classes.footer}>
                <Box className={classes.footerMeta}>
                  <AccessTimeIcon className={classes.footerIcon} />
                  {availableStartTimesCount} available start times
                </Box>
                <Box className={classes.footerMeta}>
                  <LocationOnIcon className={classes.footerIcon} />
                  {AVAILABILITY_LABEL}
                </Box>
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  )
}

export default StartTimeIncrementPreviewDialog
